import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '@radix-ui/react-switch';
import { userAPI } from '../../services/api';

const SettingsPage = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyPush, setNotifyPush] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        // Try to get settings from user profile or settings endpoint
        const response = await userAPI.getProfile();
        const user = response.data?.data?.user || response.data?.user;
        
        if (user?.settings) {
          setDarkMode(user.settings.darkMode || false);
          setNotifyEmail(user.settings.notifyEmail !== false);
          setNotifyPush(user.settings.notifyPush !== false);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        // If settings endpoint doesn't exist, use defaults
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const settingsData = {
        darkMode,
        notifyEmail,
        notifyPush
      };

      // Try to update via profile settings endpoint
      try {
        await userAPI.updateProfile({ settings: settingsData });
      } catch (err) {
        // If profile update doesn't support settings, try dedicated settings endpoint
        // This would need to be added to the API if it doesn't exist
        console.warn('Settings update via profile failed, may need dedicated endpoint');
        throw err;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Settings</h2>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-4">Loading settings...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Settings</h2>
      <Card>
        <CardContent className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-gray-600">Use dark theme across the portal</p>
            </div>
            <Switch checked={darkMode} onCheckedChange={setDarkMode} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-gray-600">Receive updates through email</p>
            </div>
            <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-gray-600">Enable real-time push notifications</p>
            </div>
            <Switch checked={notifyPush} onCheckedChange={setNotifyPush} />
          </div>

          <div className="pt-2">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;


